import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { ASSETS } from "@/constants/site";

const AREAS = [
    "Permit coordination",
    "Plan review support",
    "Pre-construction planning",
    "Architect, contractor, engineer & surveyor coordination",
    "Regulatory process support",
    "Construction document review",
    "Resubmittal coordination",
    "City and county agency communication",
];

export default function NewConstruction() {
    return (
        <div data-testid="new-construction-page">
            <section className="relative bg-[#0A192F] text-white overflow-hidden">
                <img
                    src={ASSETS.construction}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-20"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-transparent" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32">
                    <span className="overline">Specialty Practice</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-3xl">
                        New Construction
                        <br />
                        <span className="italic text-[#D4AF37]">Consultant.</span>
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        Hopewell Consulting supports property owners,
                        developers, investors, and organizations through the
                        planning, permitting, and pre-construction process in
                        Knoxville and Knox County.
                    </p>
                </div>
            </section>

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                    <figure
                        data-testid="new-construction-consulting-photo"
                        className="exec-card overflow-hidden bg-slate-100 shadow-lg"
                    >
                        <div className="w-full aspect-[16/9] overflow-hidden">
                            <img
                                src="https://customer-assets-cm19k8pv.emergentagent.net/job_hopewell-consulting/artifacts/74qdkkz4_Messenger_creation_1675F407-86C4-48B3-8512-C49F9C35B3CE.jpeg"
                                alt="Reico Hopewell reviewing a new construction floor plan and material samples with clients"
                                loading="lazy"
                                className="w-full h-full object-cover object-center"
                            />
                        </div>
                        <figcaption className="px-6 py-5 border-t border-slate-200 text-sm text-slate-600 italic leading-relaxed">
                            Design review with clients — floor plans, finish
                            samples, and every decision walked through in
                            plain language.
                        </figcaption>
                    </figure>
                </div>
            </section>

            <section className="bg-slate-50 py-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="max-w-3xl mb-12">
                        <span className="overline">Project Showcase</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Before <span className="italic text-[#D4AF37]">&amp;</span> after.
                        </h2>
                        <p className="mt-6 text-slate-700 leading-relaxed">
                            A real new construction project Reico Hopewell
                            coordinated — from undeveloped acreage and aging
                            homes to a finished, fully-permitted residential
                            subdivision. This is what we mean by{" "}
                            <em>turning vision into reality</em>.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <figure
                                className="exec-card overflow-hidden bg-white"
                                data-testid="construction-before"
                            >
                                <div
                                    className="relative aspect-video bg-slate-100 bg-no-repeat"
                                    style={{
                                        backgroundImage: `url(${ASSETS.aerialBeforeAfter})`,
                                        backgroundSize: "100% auto",
                                        backgroundPosition: "top center",
                                    }}
                                    role="img"
                                    aria-label="Aerial of the parcel before construction"
                                >
                                    <span className="absolute top-4 left-4 bg-[#0A192F] text-white text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-sm shadow">
                                        Before
                                    </span>
                                </div>
                                <figcaption className="px-5 py-4 text-xs text-slate-600 leading-relaxed border-t border-slate-100 italic">
                                    Original parcel — older homes, wooded land,
                                    no infrastructure.
                                </figcaption>
                            </figure>

                            <figure
                                className="exec-card overflow-hidden bg-white"
                                data-testid="construction-after"
                            >
                                <div
                                    className="relative aspect-video bg-slate-100 bg-no-repeat"
                                    style={{
                                        backgroundImage: `url(${ASSETS.aerialBeforeAfter})`,
                                        backgroundSize: "100% auto",
                                        backgroundPosition: "bottom center",
                                    }}
                                    role="img"
                                    aria-label="Aerial of the completed residential subdivision"
                                >
                                    <span className="absolute top-4 left-4 bg-[#D4AF37] text-[#0A192F] text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-sm shadow">
                                        After
                                    </span>
                                </div>
                                <figcaption className="px-5 py-4 text-xs text-slate-600 leading-relaxed border-t border-slate-100 italic">
                                    Completed subdivision — paved streets,
                                    driveways, and finished homes ready for
                                    occupancy.
                                </figcaption>
                            </figure>
                        </div>

                        <div className="lg:col-span-4 space-y-6">
                            <div className="exec-card p-6">
                                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                                    Scope
                                </p>
                                <p className="mt-2 font-serif text-2xl font-bold text-[#0A192F] leading-tight">
                                    Multi-home residential development
                                </p>
                            </div>
                            <div className="exec-card p-6">
                                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                                    Role
                                </p>
                                <p className="mt-2 font-serif text-2xl font-bold text-[#0A192F] leading-tight">
                                    Pre-construction & permitting consultant
                                </p>
                            </div>
                            <div className="exec-card p-6">
                                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                                    Outcome
                                </p>
                                <p className="mt-2 font-serif text-2xl font-bold text-[#0A192F] leading-tight">
                                    Permitted, built, and occupied
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <span className="overline">Scope</span>
                        <h2 className="font-serif text-4xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Eight ways we move your project forward.
                        </h2>
                        <p className="mt-6 text-slate-700 leading-relaxed">
                            We sit between the developer and the city — keeping
                            the schedule honest, the documents complete, and
                            the conversations productive.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                            {AREAS.map((a, idx) => (
                                <div
                                    key={a}
                                    className="bg-white p-6 flex items-start gap-3"
                                    data-testid={`construction-area-${idx}`}
                                >
                                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                                    <p className="text-[#0A192F] font-semibold">{a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-28">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Who we partner with</span>
                    <h2 className="font-serif text-4xl font-bold mt-4 text-[#0A192F]">
                        Built for serious owners and operators.
                    </h2>
                    <div className="mt-10 grid sm:grid-cols-3 gap-6">
                        {[
                            {
                                title: "Property Owners",
                                desc: "First-time developers turning a plot of land into a real, permitted project.",
                            },
                            {
                                title: "Investors & Developers",
                                desc: "Repeat operators looking for a Knoxville coordinator who can keep pace.",
                            },
                            {
                                title: "Organizations",
                                desc: "Nonprofits and faith-based groups building facilities, housing, or community spaces.",
                            },
                        ].map((c) => (
                            <div key={c.title} className="exec-card p-7">
                                <h3 className="font-serif text-xl font-bold text-[#0A192F]">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                    {c.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 bg-[#0A192F] text-white">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
                        Bring us in before the shovel hits the ground.
                    </h3>
                    <Link
                        to="/contact?service=New Construction Consulting"
                        data-testid="construction-cta"
                        className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest"
                    >
                        Start Your Project <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
